import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * PDP "Order by 5PM" cutoff. Liquid decides visibility from the shared preorder
 * gate plus dropship / weekend; this host mirrors that decision per variant.
 *
 * Two passes, same as the theme's optimistic PLP price:
 * 1. `change` on the picker toggles instantly from `data-hidden-variants`, so the
 *    cutoff keeps up with the PREORDER line instead of lagging the section fetch.
 * 2. `variant:update` reconciles against the server markup, which stays authoritative.
 *
 * The host is always in the DOM (may be `[hidden]`) so it can be revealed again.
 * Copy innerHTML — do not morph or replaceWith nodes from event.detail.data.html.
 */
class ProductDeliveryCutoff extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    if (!this.#scope) return;
    this.#scope.addEventListener(ThemeEvents.variantUpdate, this.#reconcile);
    this.#scope.addEventListener('change', this.#handleOptionChange);
  }

  disconnectedCallback() {
    if (!this.#scope) return;
    this.#scope.removeEventListener(ThemeEvents.variantUpdate, this.#reconcile);
    this.#scope.removeEventListener('change', this.#handleOptionChange);
    this.#scope = null;
  }

  /**
   * Instant pass — runs on the click itself, before the section fetch resolves.
   * @param {Event} event
   */
  #handleOptionChange = (event) => {
    const variantId = this.#getVariantId(event.target);
    if (!variantId) return;

    this.#setVisible(!this.#isHiddenForVariant(variantId));
  };

  /**
   * Authoritative pass — server markup wins over the optimistic guess.
   * @param {Event} event
   */
  #reconcile = (event) => {
    const variantUpdate = /** @type {VariantUpdateEvent} */ (event);
    const doc = variantUpdate.detail?.data?.html;
    if (!doc || typeof doc.querySelector !== 'function') return;

    if (variantUpdate.detail.data.newProduct) {
      this.dataset.productId = variantUpdate.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      this.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const next = this.#findMatchingCutoff(doc);
    if (!next) return;

    const nextHtml = next.innerHTML.trim();
    const nextVisible = next.getAttribute('data-visible') === 'true' && nextHtml !== '';

    if (next.dataset.message) this.dataset.message = next.dataset.message;
    if (next.dataset.hiddenVariants) this.dataset.hiddenVariants = next.dataset.hiddenVariants;
    if (next.dataset.weekendHidden) this.dataset.weekendHidden = next.dataset.weekendHidden;

    this.#setVisible(nextVisible, nextHtml);
  };

  /**
   * @param {boolean} visible
   * @param {string} [html] - Server markup; falls back to the rendered message.
   */
  #setVisible(visible, html) {
    if (!visible) {
      if (this.dataset.visible === 'false') return;
      this.dataset.visible = 'false';
      this.hidden = true;
      this.replaceChildren();
      return;
    }

    const nextHtml = html || '';
    this.dataset.visible = 'true';
    this.hidden = false;

    if (nextHtml) {
      if (this.innerHTML.trim() !== nextHtml) this.innerHTML = nextHtml;
      return;
    }

    const message = this.dataset.message || '';
    if (message && this.textContent?.trim() !== message) this.textContent = message;
  }

  /**
   * @param {string} variantId
   * @returns {boolean}
   */
  #isHiddenForVariant(variantId) {
    if (this.dataset.weekendHidden === 'true') return true;

    const hidden = this.dataset.hiddenVariants;
    if (!hidden) return false;

    return hidden.includes(`,${variantId},`);
  }

  /**
   * Reads the variant id the shopper just picked. Option radios and option
   * selects carry `data-variant-id`; the hidden `select[name="id"]` uses its value.
   * @param {EventTarget | null} target
   * @returns {string | null}
   */
  #getVariantId(target) {
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      return target.dataset.variantId || null;
    }

    if (target instanceof HTMLSelectElement) {
      const option = target.options[target.selectedIndex];
      if (option?.dataset.variantId) return option.dataset.variantId;
      if (target.name === 'id' && target.value) return target.value;
    }

    return null;
  }

  /**
   * @param {Document} doc
   * @returns {HTMLElement | null}
   */
  #findMatchingCutoff(doc) {
    const productId = this.dataset.productId;
    const scoped = productId
      ? doc.querySelector(`product-delivery-cutoff[data-product-id="${productId}"]`)
      : null;
    const match = scoped ?? doc.querySelector('product-delivery-cutoff');

    return match instanceof HTMLElement ? match : null;
  }
}

if (!customElements.get('product-delivery-cutoff')) {
  customElements.define('product-delivery-cutoff', ProductDeliveryCutoff);
}
